import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useClient } from '../../context/ClientContext';
import ClientWorkspaceLayout from '../../components/workspace/ClientWorkspaceLayout';
import { 
  Dumbbell, Plus, Edit2, Trash2, Calendar, CheckCircle, AlertCircle, RefreshCw, PowerOff, Target, Clock, Activity, Settings, Eye, Archive
} from 'lucide-react';
import { entities } from '../../api/client';
import ConfirmDialog from '../../components/training/ConfirmDialog';
import { isTourDemoActive, DEMO_USER_CODE, getDemoTrainingPlans } from '../../services/tourDemo';

function TrainingManagementPage() {
  const navigate = useNavigate();
  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();
  const { selectedClient } = useClient();

  const themeClasses = {
    ...baseThemeClasses,
    btnPrimary: `px-3 py-2.5 min-h-11 rounded-md font-semibold bg-forest-ondark text-white hover:brightness-95 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover dark:hover:brightness-100 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
    btnSecondary: `px-3 py-2.5 min-h-11 rounded-md font-medium border border-line dark:border-line-dark bg-transparent text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
    btnDanger: `px-3 py-2.5 min-h-11 rounded-md font-medium border border-red-200 dark:border-red-800 bg-transparent text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
  };

  const cardBase = `${themeClasses.bgCard} rounded-lg border border-line dark:border-line-dark`;
  const pillSm = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap';

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  const [trainingPlans, setTrainingPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const translations = {
    english: {
      'page.title': 'Client Training Plans',
      'page.createPlan': 'Create training plan',
      'page.selectClient': 'Please select a client from the top bar.',
      'page.loading': 'Loading training plans...',
      'page.noPlans': 'No training plans found for this client.',
      'page.planLabel': 'Training Plan',
      'page.createdAt': 'Created',
      'page.status.active': 'Active',
      'page.status.paused': 'Paused',
      'page.status.completed': 'Completed',
      'page.status.draft': 'Draft',
      'page.status.archived': 'Archived',
      'page.currentPlan': 'Current plan',
      'page.currentPlanHint': 'This is the training plan currently assigned to the client.',
      'page.morePlans': 'Past & Draft Plans',
      'page.morePlansHint': 'Other training plans for this client.',
      'page.noCurrentPlan': 'No active plan',
      'page.noCurrentPlanHint': 'Create or activate a training plan for this client.',
      'page.view': 'View & Edit',
      'page.pause': 'Pause',
      'page.activate': 'Activate',
      'page.delete': 'Delete',
      'page.deleteConfirm': 'Are you sure you want to delete this training plan?',
      'page.deletedSuccess': 'Training plan deleted successfully.',
      'page.deletedError': 'Error deleting training plan.',
      'goal.strength_training': 'Strength',
      'goal.muscle_building': 'Hypertrophy',
      'goal.weight_loss': 'Weight Loss',
      'goal.endurance': 'Endurance',
      'goal.general_fitness': 'General Fitness',
      'level.beginner': 'Beginner',
      'level.intermediate': 'Intermediate',
      'level.advanced': 'Advanced',
      'plan.duration': '{{weeks}} weeks',
    },
    hebrew: {
      'page.title': 'תוכניות אימון ללקוח',
      'page.createPlan': 'צור תוכנית אימון',
      'page.selectClient': 'אנא בחר לקוח מהסרגל העליון.',
      'page.loading': 'טוען תוכניות אימון...',
      'page.noPlans': 'לא נמצאו תוכניות אימון ללקוח זה.',
      'page.planLabel': 'תוכנית אימון',
      'page.createdAt': 'נוצר',
      'page.status.active': 'פעיל',
      'page.status.paused': 'מושהה',
      'page.status.completed': 'הושלם',
      'page.status.draft': 'טיוטה',
      'page.status.archived': 'בארכיון',
      'page.currentPlan': 'תוכנית נוכחית',
      'page.currentPlanHint': 'זוהי תוכנית האימונים המוקצית כעת ללקוח.',
      'page.morePlans': 'תוכניות קודמות וטיוטות',
      'page.morePlansHint': 'תוכניות אימון אחרות עבור לקוח זה.',
      'page.noCurrentPlan': 'אין תוכנית פעילה',
      'page.noCurrentPlanHint': 'צור או הפעל תוכנית אימונים ללקוח זה.',
      'page.view': 'צפה וערוך',
      'page.pause': 'השהה',
      'page.activate': 'הפעל',
      'page.delete': 'מחק',
      'page.deleteConfirm': 'האם אתה בטוח שברצונך למחוק תוכנית אימון זו?',
      'page.deletedSuccess': 'תוכנית האימון נמחקה בהצלחה.',
      'page.deletedError': 'שגיאה במחיקת תוכנית אימון.',
      'goal.strength_training': 'כוח',
      'goal.muscle_building': 'היפרטרופיה',
      'goal.weight_loss': 'ירידה במשקל',
      'goal.endurance': 'סיבולת',
      'goal.general_fitness': 'כושר כללי',
      'level.beginner': 'מתחיל',
      'level.intermediate': 'בינוני',
      'level.advanced': 'מתקדם',
      'plan.duration': '{{weeks}} שבועות',
    }
  };
  const t = (key) => translations[language]?.[key] || key;

  const loadTrainingPlans = async () => {
    if (!selectedClient) return;
    try {
      setIsLoading(true);
      const allPlans = await entities.TrainingPlans.getAll();
      let list = Array.isArray(allPlans) ? allPlans : [];
      if (isTourDemoActive()) {
        const demoPlans = getDemoTrainingPlans();
        const withoutDemo = list.filter((p) => !String(p?.id || '').startsWith('demo-'));
        list = [...demoPlans, ...withoutDemo];
      }
      
      const userCode = selectedClient.user_code || selectedClient.id;
      const filteredPlans = list.filter((plan) => plan.user_code === userCode);
      // Sort so newest are first
      filteredPlans.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTrainingPlans(filteredPlans);
    } catch (err) {
      if (isTourDemoActive()) {
        const userCode = selectedClient.user_code || selectedClient.id;
        setTrainingPlans(getDemoTrainingPlans().filter(p => p.user_code === userCode));
      } else {
        console.error('Failed to load training plans', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTrainingPlans();
  }, [selectedClient]);

  const handleUpdatePlanStatus = async (planId, status) => {
    setIsSaving(true);
    try {
      const updatedPlan = await entities.TrainingPlans.update(planId, { status });
      setTrainingPlans(trainingPlans.map(p => p.id === planId ? updatedPlan : p));
    } catch (err) {
      console.error('Failed to update plan status', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    setIsSaving(true);
    try {
      await entities.TrainingPlans.delete(planToDelete.id);
      setTrainingPlans(trainingPlans.filter(p => p.id !== planToDelete.id));
      // Just a quick visual confirmation, we don't strictly need a toast if it's clear
    } catch (err) {
      alert(t('page.deletedError'));
    } finally {
      setIsSaving(false);
      setPlanToDelete(null);
    }
  };

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  // Never show a raw key: an unknown status falls back to readable words ("on_hold" → "On hold").
  const statusLabel = (status) => {
    const key = `page.status.${status}`;
    const label = t(key);
    if (label !== key) return label;
    const words = String(status || '').replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
  };

  const renderStatusPill = (status) => {
    const label = statusLabel(status);
    let Icon = AlertCircle;
    let cls = 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
    if (status === 'archived') {
      Icon = Archive;
      cls = 'bg-moss text-ink-muted dark:bg-moss-dark dark:text-ink-muted-dark';
    } else if (status === 'active') {
      Icon = CheckCircle;
      cls = 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark';
    } else if (status === 'completed') {
      Icon = Target;
      cls = 'bg-progress-faint text-progress';
    }
    return (
      <span className={`${pillSm} ${cls}`}>
        <Icon size={11} aria-hidden />
        {label}
      </span>
    );
  };

  const renderPlanCard = (plan, { featured = false } = {}) => {
    const title = plan.plan_name || `${t('page.planLabel')} · ${new Date(plan.created_at).toLocaleDateString()}`;
    const isLive = plan.status === 'active';
    return (
      <div
        key={plan.id}
        className={`${cardBase} p-4 flex flex-col ${
          featured ? 'border-forest/40 bg-forest-faint dark:bg-forest-faint-dark sm:p-5' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-4">
          <h3
            className={`${featured ? 'text-[18px]' : 'text-[15px]'} font-semibold ${themeClasses.textPrimary} truncate`}
            title={title}
          >
            {title}
          </h3>
          {renderStatusPill(plan.status)}
        </div>

        <div className="space-y-1.5 text-[13px] mb-5 flex-1">
          <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
            <span>{t('page.createdAt')}</span>
            <span className={`${themeClasses.textPrimary} tabular-nums`}>{new Date(plan.created_at).toLocaleDateString()}</span>
          </div>
          {plan.goal && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between mt-2`}>
              <span><Target size={14} className="inline mr-1 opacity-70" />{language === 'hebrew' ? 'מטרה' : 'Goal'}</span>
              <span className={`${themeClasses.textPrimary}`}>{t(`goal.${plan.goal}`) || plan.goal}</span>
            </div>
          )}
          {plan.duration_weeks && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
              <span><Calendar size={14} className="inline mr-1 opacity-70" />{language === 'hebrew' ? 'משך' : 'Duration'}</span>
              <span className={`${themeClasses.textPrimary}`}>{t('plan.duration').replace('{{weeks}}', plan.duration_weeks)}</span>
            </div>
          )}
          {plan.difficulty_level && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
              <span><Activity size={14} className="inline mr-1 opacity-70" />{language === 'hebrew' ? 'רמה' : 'Level'}</span>
              <span className={`${themeClasses.textPrimary}`}>{t(`level.${plan.difficulty_level}`) || plan.difficulty_level}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-auto flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/training-management/builder/' + plan.id)}
            className={`${featured ? themeClasses.btnPrimary : themeClasses.btnSecondary} flex-1 min-w-[100px]`}
          >
            <Eye size={16} aria-hidden className={featured ? "" : "text-ink-muted dark:text-ink-muted-dark"} />
            {t('page.view')}
          </button>
          
          {isLive ? (
            <button
              type="button"
              onClick={() => handleUpdatePlanStatus(plan.id, 'paused')}
              disabled={isSaving}
              className={themeClasses.btnDanger + ' flex-1 min-w-[100px] !text-amber-700 !border-amber-200 hover:!bg-amber-50 dark:!text-amber-400 dark:!border-amber-800 dark:hover:!bg-amber-950/40'}
            >
              <PowerOff size={16} aria-hidden />
              <span className="truncate">{t('page.pause')}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleUpdatePlanStatus(plan.id, 'active')}
              disabled={isSaving}
              className={`${themeClasses.btnSecondary} flex-1 min-w-[100px] bg-forest-faint text-forest-ondark border-forest/30 dark:bg-forest-faint-dark`}
            >
              <CheckCircle size={16} aria-hidden />
              <span className="truncate">{t('page.activate')}</span>
            </button>
          )}

          {!isLive && (
            <button
              type="button"
              onClick={() => setPlanToDelete(plan)}
              disabled={isSaving}
              className={themeClasses.btnDanger + ' flex-none w-11 !px-0 rounded-md'}
              title={t('page.delete')}
            >
              <Trash2 size={16} aria-hidden />
            </button>
          )}
        </div>
      </div>
    );
  };

  const livePlans = trainingPlans.filter((p) => p.status === 'active');
  const otherPlans = trainingPlans.filter((p) => p.status !== 'active');

  return (
    <ClientWorkspaceLayout>
      <div className={`min-h-screen ${themeClasses.bgPrimary} font-sans pb-12`} dir={direction}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h1 className={`font-display text-4xl font-medium tracking-tight ${themeClasses.textPrimary}`}>{t('page.title')}</h1>
            <button
              type="button"
              data-tour="playbook-training-create"
              onClick={() => navigate('/training-management/builder')}
              className={themeClasses.btnPrimary}
            >
              <Plus size={16} aria-hidden />
              {t('page.createPlan')}
            </button>
          </div>

          {isLoading ? (
            <div className={`text-center ${themeClasses.textMuted} py-12 flex flex-col items-center gap-3`}>
              <RefreshCw className="w-6 h-6 text-ink-muted animate-spin" aria-hidden />
              <span className="text-[13px]">{t('page.loading')}</span>
            </div>
          ) : trainingPlans.length === 0 ? (
            <div className={`text-center ${themeClasses.textMuted} py-12 rounded-lg border border-dashed border-line dark:border-line-dark`}>
              <Dumbbell size={24} className="mx-auto mb-3 text-ink-muted" aria-hidden />
              <p className="text-[13px]">{t('page.noPlans')}</p>
            </div>
          ) : (
            <div className="space-y-8">
              <section data-tour="playbook-training-current">
                <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('page.currentPlan')}</h2>
                <p className={`${themeClasses.textMuted} text-[13px] mt-0.5 mb-3`}>{t('page.currentPlanHint')}</p>
                {livePlans.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {livePlans.map((plan) => renderPlanCard(plan, { featured: true }))}
                  </div>
                ) : (
                  <div className={`text-center py-8 px-4 rounded-lg border border-dashed border-line dark:border-line-dark ${isDarkMode ? 'bg-canvas-dark/40' : 'bg-canvas/90'}`}>
                    <PowerOff size={20} className="mx-auto mb-2 text-red-700 dark:text-red-400" aria-hidden />
                    <p className={`text-[13px] font-semibold ${themeClasses.textPrimary}`}>{t('page.noCurrentPlan')}</p>
                    <p className={`text-[13px] mt-1 ${themeClasses.textMuted}`}>{t('page.noCurrentPlanHint')}</p>
                  </div>
                )}
              </section>
              {otherPlans.length > 0 && (
                <section>
                  <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('page.morePlans')}</h2>
                  <p className={`${themeClasses.textMuted} text-[13px] mt-0.5 mb-3`}>{t('page.morePlansHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {otherPlans.map((plan) => renderPlanCard(plan))}
                  </div>
                </section>
              )}
            </div>
          )}
        </main>

        <ConfirmDialog
          isOpen={!!planToDelete}
          onClose={() => setPlanToDelete(null)}
          onConfirm={handleDeletePlan}
          title={language === 'hebrew' ? 'מחק תוכנית אימון' : 'Delete Training Plan'}
          message={t('page.deleteConfirm')}
        />
      </div>
    </ClientWorkspaceLayout>
  );
}

export default TrainingManagementPage;